import { useEffect, useRef, useState } from 'react'
import { useCollaborators, useRealtime, useRealtimeStatus, type Peer } from '@w/realtime'
import { useEditorSelectionAnchors, type BlockEditorHandle, type SelectionAnchors } from '@w/content-blocks-editor'
import { Avatar, AvatarGroup } from '@w/ui/components/avatar'
import { Popover, PopoverPopup } from '@w/ui/components/popover'
import { presenceColour, presenceColourCss } from '@w/ui/lib/presence-colour'
import { useTranslation } from '../i18n'
import { formatRelativeTime } from '../cards/relative-time'
import type { PageViewRows } from './page-views'

type RemoteSelection = Parameters<BlockEditorHandle['setRemoteSelections']>[0][number]

const MAX_AVATARS = 3

/** Every observer of the room computes the same colours (presence-colour.ts), the observer included. */
function roomUserIds(peers: readonly Peer[], ownUserId: string) {
  return [ownUserId, ...peers.map(peer => peer.identity.userId)]
}

function isSelectionAnchors(sel: unknown): sel is SelectionAnchors {
  if (typeof sel !== 'object' || sel === null) return false
  const { anchor, focus } = sel as Record<string, unknown>
  return typeof anchor === 'object' && anchor !== null && typeof focus === 'object' && focus !== null
}

/**
 * Publishes this session on `pageId` with its selection, and draws the other connections' carets in `editor`
 * (page-collaboration.spec.md). The realtime client sends "on no page" while the tab is hidden (client.ts sendState).
 */
export function PagePresence({ pageId, editor, ownUserId }: { pageId: string; editor: BlockEditorHandle | null; ownUserId: string }) {
  const client = useRealtime()
  const anchors = useEditorSelectionAnchors(editor)
  const peers = useCollaborators(pageId)
  useEffect(() => {
    client?.setLocal({ pageId, sel: anchors, active: true })
  }, [client, pageId, anchors])
  useEffect(() => () => { client?.setLocal({ pageId: null, sel: null, active: false }) }, [client])

  const room = roomUserIds(peers, ownUserId)
  const selections: RemoteSelection[] = peers.flatMap(peer => peer.state.active && isSelectionAnchors(peer.state.sel)
    ? [{ id: peer.connId, name: peer.identity.name, colour: presenceColourCss(presenceColour(peer.identity.userId, room)), anchor: peer.state.sel.anchor, focus: peer.state.sel.focus }]
    : [])
  // The editor replaces its set on every call; skip calls that would draw the same carets.
  const drawn = useRef<{ editor: BlockEditorHandle | null; key: string }>({ editor: null, key: '' })
  useEffect(() => {
    if (!editor) return
    const key = JSON.stringify(selections)
    if (drawn.current.editor === editor && drawn.current.key === key) return
    drawn.current = { editor, key }
    editor.setRemoteSelections(selections)
  })
  return null
}

/**
 * The people on this page other than the current user, as one avatar stack (flashcards-editor-home.requirements.md 2026-11-01,
 * page-collaboration.spec.md "On the page", page-views.spec.md "Showing views"): live collaborators first with their presence
 * ring (dimmed while idle), then past viewers who are not live (no ring, dimmed). At most three avatars and "+N"; clicking the
 * stack lists everyone with "now" or how long ago they viewed. Renders nothing when nobody else is live or has viewed.
 */
export function PagePeople({ pageId, ownUserId, views }: { pageId: string; ownUserId: string; views: PageViewRows | undefined }) {
  const { t } = useTranslation('page')
  const status = useRealtimeStatus()
  const peers = useCollaborators(pageId)
  const [open, setOpen] = useState(false)
  const anchor = useRef<HTMLDivElement>(null)
  const room = roomUserIds(peers, ownUserId)
  const live = new Map<string, { userId: string; name: string; avatar: string | null; active: boolean }>()
  for (const peer of peers) {
    if (peer.identity.userId === ownUserId) continue
    const seen = live.get(peer.identity.userId)
    live.set(peer.identity.userId, { userId: peer.identity.userId, name: peer.identity.name, avatar: peer.identity.avatar,
      active: (seen?.active ?? false) || peer.state.active })
  }
  const past = (views ?? []).filter(view => view.userId !== ownUserId && !live.has(view.userId))
  const now = new Date()
  const people = [
    ...[...live.values()].map(user => ({ id: user.userId, name: user.name, src: user.avatar ?? undefined,
      ringColour: presenceColour(user.userId, room), dimmed: !user.active, when: formatRelativeTime(now, now) })),
    ...past.map(view => ({ id: view.userId, name: view.name ?? view.email ?? view.userId, src: view.avatar ?? undefined,
      ringColour: undefined, dimmed: true, when: formatRelativeTime(new Date(Math.min(view.viewedAt, now.getTime())), now) })),
  ]
  const unavailable = status === 'offline' ? <span role="status" className="text-on-surface-secondary text-sm">{t('collaborationUnavailable')}</span> : null
  if (people.length === 0) return unavailable
  return <>
    {unavailable}
    {/* Each avatar is already a button (AvatarGroup), so the group opens the list from their clicks (Enter/Space included) instead of nesting buttons. */}
    <AvatarGroup ref={anchor} avatars={people} maxVisible={MAX_AVATARS} size="size-6" data-testid="flashcards-page-collaborators" aria-label={t('peopleOnPage')}
      onClick={() => { setOpen(true) }} />
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverPopup anchor={anchor} align="end" showArrow={false}>
        <ul className="flex max-h-80 flex-col gap-3 overflow-y-auto" data-testid="flashcards-page-people-list" aria-label={t('peopleOnPage')}>
          {people.map(person => (
            <li key={person.id} className="flex items-center gap-3 text-sm" data-id={person.id}>
              <Avatar size="xs" name={person.name} src={person.src} />
              <span className="text-on-surface min-w-0 flex-1 truncate">{person.name}</span>
              <span className="text-on-surface-secondary shrink-0">{person.when}</span>
            </li>
          ))}
        </ul>
      </PopoverPopup>
    </Popover>
  </>
}
